// liveTablePolicy — which roles bind which app-wide Realtime tables.
//
// REALTIME_REDESIGN_PLAN.md §4 step 1: a role opens only the tables its pages
// read. Every tab used to bind every table (a packager followed orders, twilio
// numbers and group rooms it has no page for), and Realtime runs each table's
// policy once per subscriber for every change. Derived from App.tsx's route
// `allowedRoles` plus the `useApp()` reads of each page and of the app shell
// (Sidebar, MobileBottomTabs, NotificationHandler, Settings) — 2026-09-22:
//
//   products            every role (Products / Stock Overview / hubs / Driver
//                       App / Store Hub / Packaging House / Warehouse Reports)
//   store_stocks        Dashboard, Inventory Ops/Tracker, Stock Overview, Store
//                       Stock, Orders, Store Hub; Settings shows totals only to
//                       super admin / admin
//   driver_stocks       same pages plus the Driver App; not the Store Hub
//   transactions        Inventory Ops, Store/Driver/Handoff reports, Admin
//                       Panel, Store Hub, Driver App, Warehouse hub (direct packs)
//   orders              Orders, Dashboard, Delivery Map, Board, reports, Store
//                       Hub, Driver App, chat lists; the stock manager never
//                       (0047) and the packaging-house roles have no page for
//                       them (their in-tab alerts already skip order chats)
//   twilio_numbers      Admin Panel only
//   store_temp_managers Orders (card chips), Driver App (Store tab), and the
//                       packer attribution on a store's own order writes
//   rooms               the roles with a Chat page (group chat lists + alerts)
//   packing_houses /    the Packaging House hub (which house am I in), the
//   packing_house_members  Warehouse hub (hand-off picker, per-house views)
//                       and the Admin Panel card (0080); two tiny tables
//
// The warehouse pipeline tables are a separate, page-scoped rule
// (WAREHOUSE_TABLE_ROLES + services/warehouseTables.ts). `users`,
// `user_read_state` and the dayZero settings row stay on every role.
import { UserRole } from '../types';
import { PACKAGING_HOUSE_ROLES } from './warehousePolicy';

export type AppLiveTable =
  | 'products'
  | 'store_stocks'
  | 'driver_stocks'
  | 'transactions'
  | 'orders'
  | 'twilio_numbers'
  | 'store_temp_managers'
  | 'rooms'
  | 'packing_houses'
  | 'packing_house_members';

const {
  OWNER: OW, SUPER_ADMIN: SA, ADMIN: A, DISPATCH: D, MANAGER: M, STOCK_MANAGER: SM,
  STORE: ST, DRIVER: DR, WAREHOUSE_MANAGER: WM, WORKER: W, PACKAGER: P, TEAM_MEMBER: TM,
} = UserRole;

export const ALL_ROLES: readonly UserRole[] = [OW, SA, A, D, M, SM, ST, DR, WM, W, P, TM];

// A team member (2026-09-24) has only the Task Management board, which binds
// its own tables while mounted — nothing app-wide, not even products.
const EVERYONE_BUT_TEAM: readonly UserRole[] = ALL_ROLES.filter((r) => r !== TM);

export const APP_TABLE_ROLES: Readonly<Record<AppLiveTable, readonly UserRole[]>> = {
  products: EVERYONE_BUT_TEAM,
  store_stocks: [SA, A, D, M, SM, ST],
  driver_stocks: [SA, A, D, M, SM, DR],
  transactions: [SA, A, D, M, ST, DR, WM],
  orders: [SA, A, D, M, ST, DR],
  twilio_numbers: [SA, A],
  store_temp_managers: [SA, A, D, ST, DR],
  rooms: [SA, A, D, ST, DR],
  packing_houses: [SA, A, M, WM, W, P],
  packing_house_members: [SA, A, M, WM, W, P],
};

/** Roles whose pages read the warehouse pipeline (= who can open the
 *  Packaging House, services/warehousePolicy.ts); bound only while such a
 *  page is mounted. */
export const WAREHOUSE_TABLE_ROLES: readonly UserRole[] = PACKAGING_HOUSE_ROLES;

/** Roles with a Chat page (App.tsx `/messages`) — the same set that binds
 *  `rooms`; the in-tab message alerts follow `messages` only for them. */
export const CHAT_ROLES: readonly UserRole[] = APP_TABLE_ROLES.rooms;

/** Does a signed-in `role` bind `table` at app start? A role this file does
 *  not know (a missing or legacy value) binds everything, as every role did
 *  before the policy existed — the conservative answer. */
// An owner (2026-10-17) binds exactly what a super admin binds.
const asSuper = (role: UserRole | string | undefined | null) => (role === UserRole.OWNER ? SA : role);

export function roleBindsTable(role: UserRole | string | undefined | null, table: AppLiveTable): boolean {
  role = asSuper(role);
  if (!role || !ALL_ROLES.includes(role as UserRole)) return true;
  return APP_TABLE_ROLES[table].includes(role as UserRole);
}

export function roleBindsWarehouse(role: UserRole | string | undefined | null): boolean {
  role = asSuper(role);
  return !!role && WAREHOUSE_TABLE_ROLES.includes(role as UserRole);
}

export const roleHasChat = (role: UserRole | string | undefined | null): boolean =>
  roleBindsTable(role, 'rooms');
